if ( typeof CustomSteps !== 'function' ) {

  class CustomSteps extends HTMLElement {

    connectedCallback() {
      this.steps = [...this.querySelectorAll('[data-step]')];
      this.bars = [...this.querySelectorAll('.custom-steps__bar')];
      this.current = 1;

      this.preview = this.querySelector('[data-preview]');
      this.previewImage = this.querySelector('[data-preview-image]');
      this.previewText = this.querySelector('[data-preview-text]');
      this.lines = [...this.querySelectorAll('[data-preview-line]')];
      this.inputs = [...this.querySelectorAll('[data-text-input]')];
      this.visuals = [...this.querySelectorAll('input[name="properties[Visuel]"]')];
      this.inputs.forEach(input => input.dataset.defaultPlaceholder = input.placeholder);
      this.fonts = new Map();

      try {
        this.visualsConfig = JSON.parse(this.querySelector('[data-visuals-config]')?.textContent || '[]');
      } catch (error) {
        this.visualsConfig = [];
      }

      // Navigation
      this.querySelectorAll('[data-next]').forEach(btn => btn.addEventListener('click', () => this.goTo(this.current + 1)));
      this.querySelectorAll('[data-prev]').forEach(btn => btn.addEventListener('click', () => this.goTo(this.current - 1)));
      this.querySelectorAll('[data-go-step]').forEach(btn => btn.addEventListener('click', () => this.goTo(parseInt(btn.dataset.goStep))));

      // Step 1: recipes
      this.querySelector('[data-toggle-more]')?.addEventListener('click', e => {
        const recipes = this.querySelector('[data-recipes]');
        const expanded = recipes.classList.toggle('is-expanded');
        e.currentTarget.textContent = expanded ? e.currentTarget.dataset.labelLess : e.currentTarget.dataset.labelMore;
      });
      this.querySelectorAll('input[name="properties[Recette]"]').forEach(input => {
        input.addEventListener('change', () => {
          this.hideError(1);
          this.updateRecap();
        });
        // "click" (not "change") so that clicking again on the selected option also moves on
        input.addEventListener('click', () => this.autoNext(2));
      });

      // Step 2: format (variant picker)
      try {
        this.variants = JSON.parse(this.querySelector('[data-variants-data]')?.textContent || '[]');
      } catch (error) {
        this.variants = [];
      }
      this.querySelector('.custom-format')?.addEventListener('change', () => {
        this.syncVariant();
        this.updateRecap();
        this.renderPreview();
      });
      this.querySelector('.custom-format')?.addEventListener('click', e => {
        // With several options (format + size...) the customer may still have to pick the next one
        if ( e.isTrusted && e.target.matches('input[type="radio"]') && this.querySelectorAll('.custom-format [data-js-product-variant]').length <= 1 ) this.autoNext(3);
      });

      // Step 3: visuals
      this.visuals.forEach(input => {
        input.addEventListener('change', () => {
          this.hideError(3);
          this.applyVisual(input);
          this.updateRecap();
        });
        input.addEventListener('click', () => this.autoNext(4));
      });

      // Step 4: message
      this.inputs.forEach(input => input.addEventListener('input', () => {
        this.updateText();
        this.hideError(this.steps.length);
      }));
      this.inputs.forEach(input => {
        input.addEventListener('focus', () => this.startTyping());
        input.addEventListener('blur', () => setTimeout(() => {
          if ( ! this.inputs.includes(document.activeElement) ) this.stopTyping();
        }, 100));
      });
      this.querySelector('[data-certify]')?.addEventListener('change', () => this.hideError(this.steps.length));
      this.querySelector('[data-custom-submit]')?.addEventListener('click', e => {
        if ( ! this.validateAll() ) {
          e.preventDefault();
          e.stopImmediatePropagation();
        }
      }, true);

      this.initDebug();

      if ( window.ResizeObserver && this.previewText ) {
        new ResizeObserver(() => this.fitText()).observe(this.previewText);
      }

      const checkedVisual = this.visuals.find(input => input.checked) || this.visuals[0];
      if ( checkedVisual ) this.applyVisual(checkedVisual, false);
      this.updateRecap();
      this.updateText();
    }

    autoNext(step) {
      if ( this.dataset.autoNext === 'true' ) setTimeout(() => this.goTo(step), 250);
    }

    goTo(step) {
      if ( step < 1 || step > this.steps.length || step === this.current ) return;

      // Moving forward requires every previous step to be valid
      for ( let i = 1; i < step; i++ ) {
        if ( ! this.isStepValid(i) ) {
          this.showStep(i);
          this.showError(i);
          return;
        }
      }
      this.showStep(step);
    }

    showStep(step) {
      const previous = this.current;
      this.current = step;
      this.steps.forEach(section => {
        const active = parseInt(section.dataset.step) === step;
        section.hidden = ! active;
        section.classList.toggle('is-active', active);
        section.classList.toggle('is-back', active && step < previous);
      });
      this.bars.forEach((bar, i) => {
        bar.classList.toggle('is-active', i < step);
        bar.tabIndex = i + 1 < step ? 0 : -1;
      });
      this.querySelector('[data-step-current]').textContent = step;
      this.querySelectorAll('.custom-steps__back').forEach(btn => btn.hidden = step === 1);

      if ( step === this.steps.length ) this.fitText();

      const top = this.getBoundingClientRect().top;
      if ( top < 0 || top > window.innerHeight / 3 ) {
        window.scrollTo({ top: window.scrollY + top - 120, behavior: 'smooth' });
      }
    }

    isStepValid(step) {
      if ( step === 1 ) return !! this.querySelector('input[name="properties[Recette]"]:checked');
      if ( step === 3 ) return !! this.querySelector('input[name="properties[Visuel]"]:checked');
      return true;
    }

    showError(step) {
      this.steps[step - 1]?.querySelector('[data-step-error]')?.classList.add('is-visible');
    }

    hideError(step) {
      this.steps[step - 1]?.querySelector('[data-step-error]')?.classList.remove('is-visible');
      if ( step === this.steps.length ) this.querySelector('[data-custom-error]')?.classList.remove('is-visible');
    }

    validateAll() {
      for ( let i = 1; i < this.steps.length; i++ ) {
        if ( ! this.isStepValid(i) ) {
          this.showStep(i);
          this.showError(i);
          return false;
        }
      }

      const errors = [];
      this.inputs.forEach((input, i) => {
        if ( input.value.trim() === '' ) errors.push(`Vous avez oublié de remplir la ${i === 0 ? '1ère' : '2ème'} ligne.`);
      });
      if ( ! this.querySelector('[data-certify]')?.checked ) {
        errors.push('Vous avez oublié de cocher la case de validation du message.');
      }

      const errorBox = this.querySelector('[data-custom-error]');
      if ( errors.length ) {
        errorBox.innerHTML = errors.map(error => `<p>${error}</p>`).join('');
        errorBox.classList.add('is-visible');
        return false;
      }
      errorBox.classList.remove('is-visible');
      this.updatePreviewLink();
      return true;
    }

    applyVisual(input, fit = true) {
      this.visualIndex = parseInt(input.dataset.visualIndex);
      this.renderPreview(fit);
    }

    // Mobile: keep the preview and both fields visible above the keyboard while typing
    startTyping() {
      if ( ! window.matchMedia('(max-width: 1023px)').matches ) return;
      clearTimeout(this.typingTimer);
      // wait for the keyboard to open and for the browser's own scroll to the field
      this.typingTimer = setTimeout(() => this.fitTypingLayout(), 350);
      if ( ! this.typingListener && window.visualViewport ) {
        this.typingListener = () => {
          if ( this.querySelector('.custom-message.is-typing') ) this.fitTypingLayout();
        };
        window.visualViewport.addEventListener('resize', this.typingListener);
      }
    }

    stopTyping() {
      clearTimeout(this.typingTimer);
      this.querySelector('.custom-message')?.classList.remove('is-typing');
    }

    fitTypingLayout() {
      const message = this.querySelector('.custom-message');
      const firstField = this.inputs[0]?.closest('.custom-field');
      const lastField = this.inputs[this.inputs.length - 1]?.closest('.custom-field');
      if ( ! message || ! this.preview || ! firstField || ! lastField ) return;

      const viewportHeight = window.visualViewport ? window.visualViewport.height : window.innerHeight;
      const header = document.querySelector('.sticky-header');
      const headerBottom = header && getComputedStyle(header).display !== 'none' ? Math.max(0, header.getBoundingClientRect().bottom) : 0;

      // room for the preview = visible height - sticky header - gap between preview and fields - both fields - margins
      const previewBox = this.preview.getBoundingClientRect();
      const gap = Math.max(0, firstField.getBoundingClientRect().top - previewBox.bottom);
      const fieldsHeight = lastField.getBoundingClientRect().bottom - firstField.getBoundingClientRect().top;
      const height = Math.max(140, viewportHeight - headerBottom - gap - fieldsHeight - 24);
      message.style.setProperty('--typing-preview-height', `${Math.round(height)}px`);
      message.classList.add('is-typing');

      // after the resize transition, bring the preview just under the header
      setTimeout(() => {
        const top = this.preview.getBoundingClientRect().top + window.scrollY - headerBottom - 8;
        window.scrollTo({ top, behavior: 'smooth' });
        this.fitText();
      }, 260);
    }

    // Order preparation link (hidden "_Aperçu" property): redraws this visual with the customer's text
    updatePreviewLink() {
      const field = this.querySelector('[data-preview-link]');
      if ( ! field ) return;
      const visual = this.querySelector('input[name="properties[Visuel]"]:checked');
      if ( ! visual ) {
        field.value = '';
        return;
      }
      const params = new URLSearchParams({
        view: 'apercu',
        visuel: visual.dataset.visualHandle,
        // not "format": reserved by Shopify (?format=json...), the page would 404
        contenant: this.getFormat(),
        t1: this.inputs[0]?.value.trim() || '',
        t2: this.inputs[1]?.value.trim() || ''
      });
      // Spaces as %20 (URLSearchParams would write "+")
      field.value = `${field.dataset.baseUrl}?${params.toString().replace(/\+/g, '%20')}`;
    }

    // Selects the variant matching the chosen options and updates the cart input + price right away
    // (the theme also fetches the section, but that request may fail or be slow)
    syncVariant() {
      const selected = [...this.querySelectorAll('.custom-format [data-js-product-variant]')].map(group =>
        group.querySelector('input:checked')?.value ?? group.querySelector('select')?.value
      );
      const variant = this.variants.find(v => v.options.every((option, i) => option === selected[i]));
      if ( ! variant ) return;

      const productPage = this.closest('[data-js-product-component]') || this;
      productPage.querySelectorAll('input[name="id"]').forEach(input => input.value = variant.id);

      const price = this.querySelector(`[data-variant-price="${variant.id}"]`);
      const priceBlock = this.querySelector('[data-update-block="price-extended"]');
      if ( price && priceBlock ) priceBlock.innerHTML = price.innerHTML;

      this.querySelector('[data-custom-submit]')?.classList.toggle('disabled', ! variant.available);
    }

    // "boite" when the selected format (option value or button text) mentions a box, otherwise "sachet"
    getFormat() {
      const text = [...this.querySelectorAll('.custom-format input:checked, .custom-format select')]
        .map(elm => `${elm.value} ${elm.closest('.packaging__item--icon')?.innerText || ''}`)
        .join(' ')
        .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
        .toLowerCase();
      return text.includes('boite') ? 'boite' : 'sachet';
    }

    // Step 3 preview: the "visuel_product" linked to the selected visual for the selected format
    renderPreview(fit = true) {
      const config = this.visualsConfig[this.visualIndex];
      if ( ! config || ! this.preview ) return;
      const format = this.getFormat();
      const product = config[format] || config.sachet || config.boite;
      if ( ! product ) return;

      if ( this.previewImage && product.image ) this.previewImage.src = product.image;
      this.preview.style.setProperty('--preview-ratio', product.ratio || 1);
      this.preview.style.setProperty('--text-top', `${product.top}%`);
      this.preview.style.setProperty('--text-left', `${product.left}%`);
      this.preview.style.setProperty('--text-width', `${product.width}%`);
      this.updateDebug(product, format);

      this.lines.forEach((line, i) => {
        const settings = product.lines?.[i] || {};
        line.style.setProperty('--line-size', settings.size || 8);
        line.style.color = settings.color || '';
        line.style.removeProperty('font-family');
        if ( settings.font ) {
          this.loadFont(settings.font).then(family => {
            if ( family ) {
              line.style.fontFamily = `"${family}", var(--font-stack-headings)`;
              this.fitText();
            }
          });
        }
        const input = this.inputs[i];
        if ( input ) input.placeholder = settings.placeholder || input.dataset.defaultPlaceholder;
      });

      this.updateText(fit);
    }

    // Admin helper: shows the text area and lets you try text_top / text_left / text_width values (nothing is saved)
    initDebug() {
      const toggle = this.querySelector('[data-debug-toggle]');
      this.debugPanel = this.querySelector('[data-debug-panel]');
      if ( ! toggle || ! this.debugPanel ) return;
      if ( ! this.hasAttribute('data-debug') && ! new URLSearchParams(window.location.search).has('debug_visuel') ) return;

      toggle.hidden = false;
      toggle.addEventListener('click', () => {
        const open = this.debugPanel.hidden;
        this.debugPanel.hidden = ! open;
        toggle.setAttribute('aria-expanded', open);
        this.preview.classList.toggle('is-debug', open);
      });
      this.debugPanel.querySelectorAll('[data-debug-input]').forEach(input => input.addEventListener('input', () => {
        if ( input.value === '' ) return;
        this.preview.style.setProperty(`--text-${input.dataset.debugInput}`, `${input.value}%`);
        this.fitText();
      }));
    }

    updateDebug(product, format) {
      if ( ! this.debugPanel ) return;
      this.debugPanel.querySelectorAll('[data-debug-input]').forEach(input => input.value = product[input.dataset.debugInput]);
      const label = this.debugPanel.querySelector('[data-debug-format]');
      if ( label ) label.textContent = format === 'boite' ? 'Boîte' : 'Sachet';
    }

    // Load a font file uploaded in the metaobject (woff2, woff, ttf, otf) once, returns its family name
    loadFont(url) {
      if ( ! this.fonts.has(url) ) {
        const family = `custom-visual-font-${this.fonts.size + 1}`;
        const promise = window.FontFace
          ? new FontFace(family, `url("${url}")`).load().then(font => {
              document.fonts.add(font);
              return family;
            }).catch(() => null)
          : Promise.resolve(null);
        this.fonts.set(url, promise);
      }
      return this.fonts.get(url);
    }

    updateText(fit = true) {
      this.inputs.forEach((input, i) => {
        const line = this.lines[i];
        if ( line ) {
          line.textContent = input.value || input.placeholder;
          line.classList.toggle('is-placeholder', input.value === '');
        }
        const count = this.querySelector(`[data-count="${i + 1}"]`);
        if ( count ) count.textContent = `${input.value.length}/${input.maxLength}`;
      });
      this.updatePreviewLink();
      if ( fit ) this.fitText();
    }

    // Shrink each line until it fits the text area width
    fitText() {
      if ( ! this.previewText || this.previewText.offsetWidth === 0 ) return;
      this.lines.forEach(line => {
        line.style.removeProperty('font-size');
        let size = parseFloat(getComputedStyle(line).fontSize);
        while ( line.scrollWidth > line.clientWidth && size > 8 ) {
          size -= 1;
          line.style.fontSize = `${size}px`;
        }
      });
    }

    updateRecap() {
      const recipe = this.querySelector('input[name="properties[Recette]"]:checked');
      const recipeRecap = this.querySelector('[data-recap="recipe"]');
      if ( recipeRecap ) recipeRecap.textContent = recipe ? recipe.dataset.label : '–';

      const format = this.querySelector('.custom-format input:checked, .custom-format select');
      const formatRecap = this.querySelector('[data-recap="format"]');
      if ( formatRecap ) {
        // Format title only, without what is between parentheses (e.g. "Boîte (+1€)" -> "Boîte")
        const title = format ? ( format.closest('.packaging__item--icon')?.querySelector('.label--icon-right h6')?.innerText || format.value ) : '';
        const label = title.replace(/\([^)]*\)/g, '').replace(/\s+/g, ' ').trim();
        formatRecap.textContent = label || '–';
        formatRecap.parentElement.hidden = ! format;
      }

      const visual = this.querySelector('input[name="properties[Visuel]"]:checked');
      const visualRecap = this.querySelector('[data-recap="visual"]');
      if ( visualRecap ) visualRecap.textContent = visual ? `n°${parseInt(visual.dataset.visualIndex) + 1}` : '–';
    }

  }

  if ( typeof customElements.get('custom-steps') == 'undefined' ) {
    customElements.define('custom-steps', CustomSteps);
  }

}
